import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Modal, Button } from "components/ui";
import SelectUI from "components/ui/SelectUI/SelectUI";
import { ToastContext } from "components/dashboard/common/Toast/context/ToastContextProvider";
import { apiRoute } from "services";
import API from "services/lib/api";
import { getErrorMessage } from "utils/helper";
import { dataQueryStatus } from "utils";
import { updateIncomingTicket } from "store/tickets/incomingTickets/actions";
import { setActiveTicket } from "store/tickets/actions";
import "./ChangeFormTicketModal.scss";

const { ERROR } = dataQueryStatus;

const ChangeFormTicketModal = ({ show, closeModal, selectedTicket }) => {
    const [forms, setForms] = useState([]);
    const [isLoadingForms, setIsLoadingForms] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFormId, setSelectedFormId] = useState("");
    const [serverError, setServerError] = useState("");
    const toastMessage = useContext(ToastContext);
    const dispatch = useDispatch();

    const ticketId = selectedTicket?.ticketId;

    const formOptions = useMemo(
        () =>
            (forms || []).map((form) => ({
                value: form?.formId,
                label: form?.formName,
            })),
        [forms]
    );

    const selectedOption = useMemo(
        () => formOptions.find((item) => item.value === selectedFormId),
        [formOptions, selectedFormId]
    );

    const resetState = () => {
        setSelectedFormId("");
        setServerError("");
    };

    const handleClose = () => {
        resetState();
        closeModal?.();
    };

    const getWorkspaceForms = async () => {
        try {
            setServerError("");
            setIsLoadingForms(true);
            const res = await API.get(apiRoute.getForms, {
                params: { all: true },
            });

            if (res.status === 200) {
                setForms(res?.data?.data || []);
            }
        } catch (err) {
            setServerError(getErrorMessage(err));
        } finally {
            setIsLoadingForms(false);
        }
    };

    const refreshTicketDetails = async () => {
        if (!ticketId) return;
        try {
            const detailsRes = await API.get(apiRoute.getTicketDetails(ticketId));
            if (detailsRes.status === 200) {
                const latestTicket = detailsRes?.data?.data;
                if (latestTicket?.ticketId) {
                    dispatch(updateIncomingTicket(latestTicket));
                    dispatch(setActiveTicket(latestTicket));
                }
            }
        } catch (error) {
            // Replacement already succeeded; skip UI refresh failures.
        }
    };

    const handleChangeForm = async () => {
        if (!ticketId || !selectedFormId) return;
        try {
            setIsSubmitting(true);
            const res = await API.patch(apiRoute.replaceTicketFormInfo(ticketId), {
                formId: selectedFormId,
            });

            if (res.status === 200) {
                await refreshTicketDetails();
                toastMessage(res?.data?.message || "Ticket form changed successfully");
                handleClose();
            }
        } catch (err) {
            toastMessage(getErrorMessage(err), ERROR);
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        if (show) {
            getWorkspaceForms();
        } else {
            resetState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    return (
        <Modal
            show={show}
            close={handleClose}
            size='md'
            centered
            contentClassName='change-form-ticket-modal__content'>
            <div className='change-form-ticket-modal'>
                <div className='change-form-ticket-modal__header'>
                    <div>
                        <h6 className='change-form-ticket-modal__title'>Change form on ticket</h6>
                        <p className='change-form-ticket-modal__subtitle'>
                            Choose ticket info from dropdown
                        </p>
                    </div>
                    <button
                        type='button'
                        className='btn-close'
                        aria-label='Close'
                        onClick={handleClose}
                    />
                </div>
                <div className='change-form-ticket-modal__actions'>
                    <div className='row g-0 w-100'>
                        <div className='col-8'>
                            <div className='change-form-ticket-modal__select'>
                                <SelectUI
                                    name='formId'
                                    placeholder='Search and select form'
                                    options={formOptions}
                                    value={selectedOption || null}
                                    isLoading={isLoadingForms}
                                    serverError={serverError}
                                    handleRetry={getWorkspaceForms}
                                    onChange={(option) => setSelectedFormId(option?.value || "")}
                                    marginClass='mb-0'
                                />
                            </div>
                        </div>
                        <div className='col-4'>
                            <Button
                                type='button'
                                text='Change Form'
                                classType='apply'
                                onClick={handleChangeForm}
                                disabled={!selectedFormId || isSubmitting || isLoadingForms}
                                loading={isSubmitting}
                                otherClass='w-100 h-100 change-form-ticket-modal__submit'
                            />
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ChangeFormTicketModal;

